/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import type { PropsWithChildren } from 'react';

import { Typography2, breakpoints } from '@coursera/cds-core';

import EditorNotification from 'bundles/personalization-settings/components/EditorNotification';

import _t from 'i18n!nls/personalization-settings';

const styles = {
  row: css`
    display: grid;
    grid-template-columns: 240px minmax(0, 1fr);
    gap: var(--cds-spacing-100);
    align-items: center;

    ${breakpoints.down('sm')} {
      grid-template-columns: minmax(0, 1fr);
    }
  `,
  content: css`
    min-width: 0;
  `,
  rowContainer: css`
    display: grid;
    gap: var(--cds-spacing-200);
  `,
  skeletonInput: css`
    border: solid 1px var(--cds-color-neutral-disabled);
    border-radius: var(--cds-border-radius-100);
    height: 48px;
    background-color: var(--cds-color-neutral-background-primary);
  `,
};

export type SettingRowProps = PropsWithChildren<{
  /** The field label, shown in the left column. */
  label: string;
  /** The label id that we can use for accessibility */
  labelId?: string;
  /** The current save status for this row; renders as an announced notification below the editor. */
  notificationType?: 'success' | 'error';
  /** Loading shows the loading where the content would be */
  loading?: boolean;
  /** Overrides the default save-status text for `notificationType`, e.g. a validation message. */
  notificationMessage?: string;
  /**
   * Remounts the notification when changed, so an aria-live region re-announces
   * identical text -- e.g. repeated limit-exceeded attempts with the same message.
   */
  notificationKey?: string | number;
  /** A follow-up action rendered inside the notification, e.g. an undo. */
  notificationAction?: { label: string; onClick: () => void; autoFocus?: boolean };
  /**
   * Called with true while the learner's focus or pointer is on the notification,
   * and false once both leave, so a timed notification can wait for them.
   */
  onNotificationHoldChange?: (held: boolean) => void;
}>;

const SettingRow = ({
  label,
  labelId,
  notificationType,
  notificationMessage,
  notificationKey,
  notificationAction,
  onNotificationHoldChange,
  loading,
  children,
}: SettingRowProps) => {
  const notificationRegionRef = React.useRef<HTMLDivElement>(null);
  const [focused, setFocused] = React.useState(false);
  const [hovered, setHovered] = React.useState(false);

  // A notification that unmounts takes its focused control with it without a
  // blur event, so re-read focus from the DOM whenever the notification changes.
  React.useEffect(() => {
    setFocused(notificationRegionRef.current?.contains(document.activeElement) ?? false);
  }, [notificationKey, notificationType]);

  const held = focused || hovered;
  React.useEffect(() => {
    onNotificationHoldChange?.(held);
  }, [held, onNotificationHoldChange]);

  return (
    <div css={styles.rowContainer}>
      <div css={styles.row}>
        <Typography2 component="p" variant="subtitleMedium" id={labelId}>
          {label}
        </Typography2>
        {loading ? (
          <div
            css={styles.skeletonInput}
            role="status"
            aria-label={_t('Loading')}
            data-testid="personalization-setting-loading"
          />
        ) : (
          <div css={styles.content}>{children}</div>
        )}
      </div>
      <div
        ref={notificationRegionRef}
        aria-live="polite"
        onFocus={() => setFocused(true)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(false);
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {notificationType && (
          <EditorNotification
            key={notificationKey}
            type={notificationType}
            message={notificationMessage}
            action={notificationAction}
          />
        )}
      </div>
    </div>
  );
};

export default SettingRow;
